import { Segmented } from '@/components/ui/segmented';
import { formatDate } from '@/lib/formatDate';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import {
  getInvoiceStats, getCollections, getOutstanding, getRevenueByDoctor,
  getAppointmentFootfall, getAppointmentsByDoctor, getOccupancySummary, getLengthOfStay,
  getPatientStats,
  type InvoiceStats, type CollectionRow, type OutstandingRow, type DoctorRevenueRow,
  type AppointmentFootfallRow, type DoctorAppointmentRow, type WardOccupancyRow,
  type LengthOfStayStats, type PatientStatsResult,
} from '@/lib/reportsApi';
import { BarChart3, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { addDays, differenceInCalendarDays, format, parseISO, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, LineChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { usePageTitle } from "@/hooks/usePageTitle";

const todayStr = () => format(new Date(), 'yyyy-MM-dd');
const monthStartStr = () => format(startOfMonth(new Date()), 'yyyy-MM-dd');

const inr = (n: number | null | undefined) => `₹${Number(n ?? 0).toLocaleString('en-IN')}`;
// "2026-09-01" → "1 Sep 2026"
const niceDate = (d: string) => formatDate(d);
const pct = (num: number, denom: number) => denom > 0 ? `${((num / denom) * 100).toFixed(1)}%` : '—';
const compactNum = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(n);
const dateTick = (v: string) => format(new Date(v), 'dd MMM');

/** Every day from `from` to `to` in a daily chart, a quiet day as zeros — without them 01 Oct and 09 Oct
 *  sit side by side as if they were consecutive. Ranges over two months keep only the days with data. */
function fillDays<T extends { date: string }>(rows: T[], from: string, to: string, zero: Omit<T, 'date'>): T[] {
  const byDay = new Map(rows.map(r => [String(r.date).slice(0, 10), r]));
  const start = parseISO(from), days = differenceInCalendarDays(parseISO(to), start);
  if (!from || !to || days < 0 || days > 62) return [...rows].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  return Array.from({ length: days + 1 }, (_, i) => {
    const day = format(addDays(start, i), 'yyyy-MM-dd');
    return byDay.get(day) ?? ({ ...zero, date: day } as T);
  });
}

// Chart colours come from the theme tokens, so charts follow light and dark mode.
// Teal is the main series, slate the comparison, status colours only where they mean something.
const C = {
  primary: 'hsl(var(--primary))',
  primarySoft: 'hsl(var(--primary) / 0.5)',
  info: 'hsl(var(--info))',
  slate: 'hsl(var(--muted-foreground) / 0.45)',
  alert: 'hsl(var(--destructive))',
};
const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };
const GRID = { stroke: 'hsl(var(--border))', vertical: false } as const;
const TOOLTIP = {
  contentStyle: {
    background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 6,
    fontSize: 12, color: 'hsl(var(--popover-foreground))', boxShadow: 'none',
  },
  labelStyle: { color: 'hsl(var(--muted-foreground))', marginBottom: 2 },
  cursor: { fill: 'hsl(var(--muted) / 0.6)' },
};
const LEGEND = { wrapperStyle: { fontSize: 12, color: 'hsl(var(--muted-foreground))' }, iconSize: 8, iconType: 'square' as const };
const BAR_RADIUS: [number, number, number, number] = [2, 2, 0, 0];

type Preset = 'today' | 'week' | 'month' | 'last_month' | 'custom';

function getPresetDates(preset: Preset): { from: string; to: string } {
  const today = new Date();
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd');
  switch (preset) {
    case 'today':
      return { from: fmt(today), to: fmt(today) };
    case 'week':
      return { from: fmt(startOfWeek(today, { weekStartsOn: 1 })), to: fmt(today) };
    case 'month':
      return { from: fmt(startOfMonth(today)), to: fmt(today) };
    case 'last_month': {
      const lm = subMonths(today, 1);
      return { from: fmt(startOfMonth(lm)), to: fmt(new Date(lm.getFullYear(), lm.getMonth() + 1, 0)) };
    }
    default:
      return { from: monthStartStr(), to: fmt(today) };
  }
}

// ─── Building blocks ──────────────────────────────────────────────────────────

/** A row of headline figures in one card; the grid lines are the 1px gap over the border colour. */
function Figures({ items, loading }: {
  items: { label: string; value: string; sub?: string; due?: boolean }[];
  loading?: boolean;
}) {
  const cols = items.length === 3 ? 'md:grid-cols-3' : items.length >= 6 ? 'md:grid-cols-3 xl:grid-cols-6' : 'md:grid-cols-4';
  return (
    <dl className={cn('grid grid-cols-2 gap-px bg-border rounded-lg border border-border overflow-hidden', cols)}>
      {items.map(f => (
        <div key={f.label} className="bg-card px-4 py-3 min-w-0">
          <dt className="text-xs text-muted-foreground">{f.label}</dt>
          <dd className="mt-0.5">
            {loading ? (
              <span className="block h-7 w-20 rounded bg-muted animate-pulse" />
            ) : (
              <span className={cn('font-mono text-xl font-semibold tabular-nums', f.due ? 'rounded bg-highlight px-1.5 text-highlight-foreground' : 'text-foreground')}>
                {f.value}
              </span>
            )}
            {f.sub && <span className="block text-xs text-muted-foreground">{f.sub}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** One report: a titled card with an optional chart above its table. */
function ReportCard({ title, meta, action, chart, children }: {
  title: string; meta?: React.ReactNode; action?: React.ReactNode; chart?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <section aria-label={title} className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 min-h-11 px-4 py-2 border-b border-border">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          {meta && <p className="text-xs text-muted-foreground tabular-nums">{meta}</p>}
        </div>
        {action}
      </div>
      {chart && <div className="h-56 px-2 pt-3 pb-1 border-b border-border">{chart}</div>}
      {children}
    </section>
  );
}

type Col = { label: string; numeric?: boolean };

/** Report table: 32px header, 40px rows, numbers right-aligned in mono under right-aligned headings. */
function ReportTable({ cols, children, footer }: { cols: Col[]; children: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="h-8 border-b border-border bg-muted/40 text-2xs uppercase tracking-wide text-muted-foreground">
            {cols.map((c, i) => (
              <th key={c.label} scope="col" className={cn('px-2 sm:px-3 font-medium whitespace-nowrap', i === 0 && 'pl-4 sm:pl-4', i === cols.length - 1 && 'pr-4 sm:pr-4', c.numeric ? 'text-right' : 'text-left')}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </table>
    </div>
  );
}

const ROW = 'h-10 border-b border-divider last:border-0';
const FOOT = 'h-10 border-t border-border bg-muted/40 font-semibold';
/** First, middle and last cell padding match the header */
const tdFirst = 'pl-4 pr-2 sm:pr-3';
const tdLast = 'pl-2 sm:pl-3 pr-4';
function Num({ value, className, last }: { value: React.ReactNode; className?: string; last?: boolean }) {
  return <td className={cn(last ? tdLast : 'px-2 sm:px-3', 'text-right font-mono tabular-nums whitespace-nowrap', className)}>{value}</td>;
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-4 py-6 text-sm text-muted-foreground">{children}</p>;
}

function ChartLoading() {
  return <div className="h-full rounded bg-muted/40 animate-pulse" />;
}

function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center h-10 px-4 border-b border-divider last:border-0">
          <div className="h-3.5 w-full rounded bg-muted animate-pulse" />
        </div>
      ))}
    </div>
  );
}

const PRESETS: { key: Preset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
];

export default function ReportsPage() {
  usePageTitle('Reports');
  const { currentUser } = useAuth();
  const { hasFeature, can } = usePermission();
  const canViewIpd = hasFeature('ipd') && can('ipd:read');
  const canViewSummary = can('reports:summary');

  const [activeTab, setActiveTab] = useState<'financial' | 'patients' | 'appointments' | 'ipd'>('financial');
  const [preset, setPreset] = useState<Preset>('month');
  const [dateFrom, setDateFrom] = useState(monthStartStr());
  const [dateTo, setDateTo] = useState(todayStr());

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') {
      const { from, to } = getPresetDates(p);
      setDateFrom(from);
      setDateTo(to);
    }
  };

  const handleDateChange = (field: 'from' | 'to', val: string) => {
    setPreset('custom');
    if (field === 'from') setDateFrom(val);
    else setDateTo(val);
  };

  // Financial
  const [stats, setStats] = useState<InvoiceStats | null>(null);
  const [collections, setCollections] = useState<CollectionRow[]>([]);
  const [outstanding, setOutstanding] = useState<OutstandingRow[]>([]);
  const [byDoctor, setByDoctor] = useState<DoctorRevenueRow[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Patients
  const [patientData, setPatientData] = useState<PatientStatsResult | null>(null);
  const [patientsLoading, setPatientsLoading] = useState(true);

  // Appointments
  const [footfall, setFootfall] = useState<AppointmentFootfallRow[]>([]);
  const [doctorAppts, setDoctorAppts] = useState<DoctorAppointmentRow[]>([]);
  const [apptsLoading, setApptsLoading] = useState(true);

  // IPD
  const [occupancy, setOccupancy] = useState<WardOccupancyRow[]>([]);
  const [los, setLos] = useState<LengthOfStayStats | null>(null);
  const [ipdLoading, setIpdLoading] = useState(true);

  const load = useCallback(() => {
    if (!currentUser?.clinic_id) return;
    setLoading(true);
    Promise.all([
      canViewSummary ? getInvoiceStats(currentUser.clinic_id, { dateFrom, dateTo }) : Promise.resolve(null),
      canViewSummary ? getCollections(currentUser.clinic_id, { dateFrom, dateTo }) : Promise.resolve([]),
      getOutstanding(currentUser.clinic_id),
      canViewSummary ? getRevenueByDoctor(currentUser.clinic_id, { dateFrom, dateTo }) : Promise.resolve([]),
    ])
      .then(([s, c, o, d]) => {
        setStats(s ?? null);
        setCollections(c ?? []);
        setOutstanding(o ?? []);
        setByDoctor(d ?? []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentUser?.clinic_id, dateFrom, dateTo, canViewSummary]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (activeTab !== 'patients' || !currentUser?.clinic_id) return;
    setPatientsLoading(true);
    getPatientStats(currentUser.clinic_id, { dateFrom, dateTo })
      .then(d => setPatientData(d))
      .catch(console.error)
      .finally(() => setPatientsLoading(false));
  }, [activeTab, currentUser?.clinic_id, dateFrom, dateTo]);

  useEffect(() => {
    if (activeTab !== 'appointments' || !currentUser?.clinic_id) return;
    setApptsLoading(true);
    Promise.all([
      getAppointmentFootfall(currentUser.clinic_id, { dateFrom, dateTo }),
      getAppointmentsByDoctor(currentUser.clinic_id, { dateFrom, dateTo }),
    ])
      .then(([f, d]) => {
        setFootfall(f ?? []);
        setDoctorAppts(d ?? []);
      })
      .catch(console.error)
      .finally(() => setApptsLoading(false));
  }, [activeTab, currentUser?.clinic_id, dateFrom, dateTo]);

  useEffect(() => {
    if (activeTab !== 'ipd' || !canViewIpd || !currentUser?.clinic_id) return;
    setIpdLoading(true);
    getOccupancySummary(currentUser.clinic_id)
      .then(o => setOccupancy(o ?? []))
      .catch(console.error)
      .finally(() => setIpdLoading(false));
  }, [activeTab, canViewIpd, currentUser?.clinic_id]);

  useEffect(() => {
    if (activeTab !== 'ipd' || !canViewIpd || !currentUser?.clinic_id) return;
    getLengthOfStay(currentUser.clinic_id, { dateFrom, dateTo })
      .then(l => setLos(l ?? null))
      .catch(console.error);
  }, [activeTab, canViewIpd, currentUser?.clinic_id, dateFrom, dateTo]);

  useEffect(() => {
    if (activeTab === 'ipd' && !canViewIpd) setActiveTab('financial');
  }, [activeTab, canViewIpd]);

  if (!hasFeature('reports')) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <BarChart3 className="w-8 h-8 text-muted-foreground mb-2" />
        <h2 className="text-base font-semibold mb-1">Reports Not Available</h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          Reporting is not included in your current plan. Contact support to upgrade.
        </p>
      </div>
    );
  }

  const filteredOutstanding = outstanding.filter(o => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return o.patient_name?.toLowerCase().includes(q) || o.patient_mrn?.toLowerCase().includes(q);
  });

  const collectionTotals = collections.reduce(
    (acc, c) => {
      acc.cash += Number(c.cash); acc.upi += Number(c.upi); acc.card += Number(c.card);
      acc.netbanking += Number(c.netbanking); acc.cheque += Number(c.cheque);
      acc.other += Number(c.other); acc.total += Number(c.total); acc.refunded += Number(c.refunded || 0);
      return acc;
    },
    { cash: 0, upi: 0, card: 0, netbanking: 0, cheque: 0, other: 0, total: 0, refunded: 0 }
  );

  const apptTotals = footfall.reduce(
    (acc, f) => {
      acc.total += Number(f.total); acc.completed += Number(f.completed);
      acc.no_show += Number(f.no_show); acc.cancelled += Number(f.cancelled);
      acc.upcoming += Number(f.upcoming);
      return acc;
    },
    { total: 0, completed: 0, no_show: 0, cancelled: 0, upcoming: 0 }
  );

  const occupancyTotals = occupancy.reduce(
    (acc, w) => {
      acc.total_beds += Number(w.total_beds); acc.occupied_beds += Number(w.occupied_beds);
      return acc;
    },
    { total_beds: 0, occupied_beds: 0 }
  );

  // Oldest day first, left to right (the table below lists the newest first)
  const collectionsChartData = fillDays(collections.map(c => ({
    date: c.date,
    cash: Number(c.cash), upi: Number(c.upi), card: Number(c.card),
    other: Number(c.netbanking) + Number(c.cheque) + Number(c.other),
    total: Number(c.total),
  })), dateFrom, dateTo, { cash: 0, upi: 0, card: 0, other: 0, total: 0 });

  const byDoctorChartData = byDoctor.map(d => ({
    doctor: d.doctor_name ?? 'Unassigned',
    revenue: Number(d.total_revenue), collected: Number(d.collected_amount),
  }));

  const footfallChartData = fillDays(footfall.map(f => ({
    date: f.date, total: Number(f.total), completed: Number(f.completed), no_show: Number(f.no_show),
  })), dateFrom, dateTo, { total: 0, completed: 0, no_show: 0 });

  const doctorApptsChartData = doctorAppts.map(d => ({
    doctor: d.doctor_name ?? 'Unassigned',
    total: Number(d.total_appointments), no_show: Number(d.no_show_count),
  }));

  const occupancyChartData = occupancy.map(w => ({
    ward: w.ward_name, occupied: Number(w.occupied_beds), total: Number(w.total_beds),
  }));

  const patientDailyChartData = fillDays((patientData?.daily ?? []).map(r => ({
    date: r.date, new_patients: Number(r.new_patients),
  })), dateFrom, dateTo, { new_patients: 0 });

  const patientByDoctorChartData = (patientData?.byDoctor ?? []).map(r => ({
    doctor: r.doctor_name ?? 'Unassigned',
    new: Number(r.new_count), returning: Number(r.returning_count),
  }));

  const outstandingTotal = filteredOutstanding.reduce((sum, o) => sum + Number(o.balance_due || 0), 0);
  const TAB_TRIGGER = 'rounded-none px-0 pb-2 pt-1 touch:min-h-10 text-sm border-b-2 border-transparent text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none';
  const periodLabel = dateFrom === dateTo ? niceDate(dateFrom) : `${niceDate(dateFrom)} – ${niceDate(dateTo)}`;

  return (
    <Tabs value={activeTab} onValueChange={v => setActiveTab(v as typeof activeTab)} className="flex flex-col h-full">
      {/* Header: title, date range, presets; then the tabs */}
      <div className="flex flex-col gap-3 px-4 sm:px-6 pt-4 border-b border-border shrink-0 bg-background">
        <PageHeader icon={BarChart3} title="Reports" subtitle={periodLabel} actions={
          <div className="flex items-center gap-2 min-w-0">
            <Input
              type="date"
              aria-label="From"
              value={dateFrom}
              onChange={e => handleDateChange('from', e.target.value)}
              className="h-8 text-sm w-[9.5rem]"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              aria-label="To"
              value={dateTo}
              onChange={e => handleDateChange('to', e.target.value)}
              className="h-8 text-sm w-[9.5rem]"
            />
          </div>
        } />
        <Segmented
          aria-label="Date range"
          className="self-start max-w-full overflow-x-auto"
          options={PRESETS.map(p => ({ value: p.key, label: p.label }))}
          value={preset}
          onChange={applyPreset}
        />
        <TabsList className="h-auto bg-transparent p-0 gap-5 rounded-none justify-start self-start">
            <TabsTrigger value="financial" className={TAB_TRIGGER}>Financial</TabsTrigger>
            <TabsTrigger value="patients" className={TAB_TRIGGER}>Patients</TabsTrigger>
            <TabsTrigger value="appointments" className={TAB_TRIGGER}>Appointments</TabsTrigger>
            {canViewIpd && <TabsTrigger value="ipd" className={TAB_TRIGGER}>IPD</TabsTrigger>}
        </TabsList>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">

        {/* ── Financial ───────────────────────────────────────────────────────── */}
        <TabsContent value="financial" className="flex-1 overflow-auto px-4 sm:px-6 py-4 space-y-4 mt-0">
          {canViewSummary && (
            <Figures loading={loading} items={[
              { label: 'Billed', value: inr(stats?.total_revenue), sub: `${stats?.total_invoices ?? 0} invoice${Number(stats?.total_invoices ?? 0) === 1 ? '' : 's'}` },
              { label: 'Paid', value: inr(stats?.collected_amount), sub: Number(stats?.total_revenue ?? 0) > 0 ? `${pct(Number(stats?.collected_amount ?? 0), Number(stats?.total_revenue ?? 0))} of billed` : undefined },
              { label: 'Outstanding', value: inr(stats?.pending_amount), due: Number(stats?.pending_amount ?? 0) > 0, sub: 'on these invoices' },
              { label: 'Invoices', value: String(stats?.total_invoices ?? 0), sub: periodLabel },
            ]} />
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            {canViewSummary && (
              <ReportCard
                title="Money received"
                meta={collections.length ? `${inr(collectionTotals.total)}${collectionTotals.refunded > 0 ? ` · ${inr(collectionTotals.refunded)} refunded` : ''} · deposits included, earlier bills too` : undefined}
                chart={loading ? <ChartLoading /> : collections.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={collectionsChartData}>
                      <CartesianGrid {...GRID} />
                      <XAxis dataKey="date" tickFormatter={dateTick} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={compactNum} tick={AXIS_TICK} width={40} axisLine={false} tickLine={false} />
                      <Tooltip {...TOOLTIP} labelFormatter={dateTick} formatter={(v: number) => inr(v)} />
                      <Legend {...LEGEND} />
                      <Bar dataKey="cash" name="Cash" stackId="a" fill={C.primary} />
                      <Bar dataKey="upi" name="UPI" stackId="a" fill={C.primarySoft} />
                      <Bar dataKey="card" name="Card" stackId="a" fill={C.info} />
                      <Bar dataKey="other" name="Other" stackId="a" fill={C.slate} radius={BAR_RADIUS} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : undefined}
              >
                {loading ? <SkeletonRows /> : collections.length === 0 ? <Empty>No money received in this period.</Empty> : (
                  <ReportTable
                    cols={[{ label: 'Date' }, { label: 'Cash', numeric: true }, { label: 'UPI', numeric: true }, { label: 'Card', numeric: true }, { label: 'Other', numeric: true }, { label: 'Total', numeric: true }]}
                    footer={
                      <tr className={FOOT}>
                        <td className={tdFirst}>Total</td>
                        <Num value={inr(collectionTotals.cash)} />
                        <Num value={inr(collectionTotals.upi)} />
                        <Num value={inr(collectionTotals.card)} />
                        <Num value={inr(collectionTotals.netbanking + collectionTotals.cheque + collectionTotals.other)} />
                        <Num value={inr(collectionTotals.total)} last />
                      </tr>
                    }
                  >
                    {collections.map(c => (
                      <tr key={c.date} className={ROW}>
                        <td className={cn(tdFirst, 'whitespace-nowrap text-foreground tabular-nums')}>{niceDate(c.date)}</td>
                        <Num value={inr(c.cash)} />
                        <Num value={inr(c.upi)} />
                        <Num value={inr(c.card)} />
                        <Num value={inr(Number(c.netbanking) + Number(c.cheque) + Number(c.other))} />
                        <Num value={inr(c.total)} className="font-semibold" last />
                      </tr>
                    ))}
                  </ReportTable>
                )}
              </ReportCard>
            )}

            {canViewSummary && (
              <ReportCard
                title="Revenue by doctor"
                chart={loading ? <ChartLoading /> : byDoctorChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byDoctorChartData} margin={{ bottom: 20 }}>
                      <CartesianGrid {...GRID} />
                      <XAxis dataKey="doctor" tick={AXIS_TICK} angle={-20} textAnchor="end" height={40} interval={0} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={compactNum} tick={AXIS_TICK} width={40} axisLine={false} tickLine={false} />
                      <Tooltip {...TOOLTIP} formatter={(v: number) => inr(v)} />
                      <Legend {...LEGEND} />
                      <Bar dataKey="revenue" name="Billed" fill={C.slate} radius={BAR_RADIUS} />
                      <Bar dataKey="collected" name="Paid" fill={C.primary} radius={BAR_RADIUS} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : undefined}
              >
                {loading ? <SkeletonRows /> : byDoctor.length === 0 ? <Empty>No invoices in this period.</Empty> : (
                  <ReportTable cols={[{ label: 'Doctor' }, { label: 'Invoices', numeric: true }, { label: 'Billed', numeric: true }, { label: 'Paid', numeric: true }, { label: 'Due', numeric: true }]}>
                    {byDoctor.map(d => (
                      <tr key={d.doctor_id ?? 'unassigned'} className={ROW}>
                        <td className={cn(tdFirst, 'text-foreground')}>{d.doctor_name ?? 'Unassigned'}</td>
                        <Num value={d.invoice_count} />
                        <Num value={inr(d.total_revenue)} />
                        <Num value={inr(d.collected_amount)} />
                        <Num value={inr(d.balance_due)} last className={Number(d.balance_due) > 0 ? 'text-highlight-foreground font-medium' : 'text-muted-foreground'} />
                      </tr>
                    ))}
                  </ReportTable>
                )}
              </ReportCard>
            )}
          </div>

          <ReportCard
            title="All outstanding dues"
            // Not limited to the dates above: every unpaid balance, whenever it was billed
            meta={`All dates · ${inr(outstandingTotal)} across ${filteredOutstanding.length} patient${filteredOutstanding.length === 1 ? '' : 's'}`}
            action={
              <div className="relative w-full sm:w-56">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search patient, MRN…"
                  aria-label="Search outstanding dues"
                  className="h-8 pl-8 text-sm"
                />
              </div>
            }
          >
            {loading ? <SkeletonRows /> : filteredOutstanding.length === 0 ? (
              <Empty>{search ? 'No matching patients.' : 'No outstanding dues.'}</Empty>
            ) : (
              <ReportTable cols={[{ label: 'Patient' }, { label: 'Phone' }, { label: 'Last activity' }, { label: 'Due', numeric: true }]}>
                {filteredOutstanding.map(o => (
                  <tr key={o.patient_id} className={ROW}>
                    <td className={cn(tdFirst, 'whitespace-nowrap')}>
                      <span className="text-foreground capitalize">{o.patient_name}</span>
                      {o.patient_mrn && <span className="ml-2 font-mono text-xs text-muted-foreground">{o.patient_mrn}</span>}
                    </td>
                    <td className="px-3 whitespace-nowrap text-xs text-muted-foreground tabular-nums">{o.phone || '—'}</td>
                    <td className="px-3 whitespace-nowrap text-xs text-muted-foreground tabular-nums">
                      {o.last_transaction_date ? niceDate(o.last_transaction_date) : '—'}
                    </td>
                    <Num value={inr(o.balance_due)} last className="text-highlight-foreground font-semibold" />
                  </tr>
                ))}
              </ReportTable>
            )}
          </ReportCard>
        </TabsContent>

        {/* ── Patients ────────────────────────────────────────────────────────── */}
        <TabsContent value="patients" className="flex-1 overflow-auto px-4 sm:px-6 py-4 space-y-4 mt-0">
          <Figures loading={patientsLoading} items={[
            { label: 'Patients seen', value: String(patientData?.summary?.total_patients ?? 0) },
            { label: 'New', value: String(patientData?.summary?.new_patients ?? 0), sub: pct(Number(patientData?.summary?.new_patients ?? 0), Number(patientData?.summary?.total_patients ?? 0)) },
            { label: 'Returning', value: String(patientData?.summary?.returning_patients ?? 0), sub: pct(Number(patientData?.summary?.returning_patients ?? 0), Number(patientData?.summary?.total_patients ?? 0)) },
            { label: 'Average visits', value: patientData?.summary?.avg_visits != null ? Number(patientData.summary.avg_visits).toFixed(1) : '—', sub: 'per patient' },
          ]} />

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            <ReportCard
              title="Daily registrations"
              meta={`${(patientData?.daily ?? []).reduce((n, r) => n + Number(r.new_patients), 0)} registered`}
              chart={patientsLoading ? <ChartLoading /> : (patientData?.daily?.length ?? 0) > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={patientDailyChartData}>
                    <CartesianGrid {...GRID} />
                    <XAxis dataKey="date" tickFormatter={dateTick} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                    <YAxis tick={AXIS_TICK} width={30} allowDecimals={false} axisLine={false} tickLine={false} />
                    <Tooltip {...TOOLTIP} labelFormatter={dateTick} />
                    <Bar dataKey="new_patients" name="Registered" fill={C.primary} radius={BAR_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              ) : undefined}
            >
              {patientsLoading ? <SkeletonRows /> : (patientData?.daily ?? []).length === 0 ? <Empty>No registrations in this period.</Empty> : (
                <ReportTable cols={[{ label: 'Date' }, { label: 'Registered', numeric: true }]}>
                  {(patientData?.daily ?? []).map(r => (
                    <tr key={r.date} className={ROW}>
                      <td className={cn(tdFirst, 'whitespace-nowrap text-foreground tabular-nums')}>{niceDate(r.date)}</td>
                      <Num value={r.new_patients} last />
                    </tr>
                  ))}
                </ReportTable>
              )}
            </ReportCard>

            <ReportCard
              title="By doctor"
              chart={patientsLoading ? <ChartLoading /> : patientByDoctorChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={patientByDoctorChartData} margin={{ bottom: 20 }}>
                    <CartesianGrid {...GRID} />
                    <XAxis dataKey="doctor" tick={AXIS_TICK} angle={-20} textAnchor="end" height={40} interval={0} axisLine={false} tickLine={false} />
                    <YAxis tick={AXIS_TICK} width={30} allowDecimals={false} axisLine={false} tickLine={false} />
                    <Tooltip {...TOOLTIP} />
                    <Legend {...LEGEND} />
                    <Bar dataKey="new" name="New" fill={C.primary} radius={BAR_RADIUS} />
                    <Bar dataKey="returning" name="Returning" fill={C.info} radius={BAR_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              ) : undefined}
            >
              {patientsLoading ? <SkeletonRows /> : (patientData?.byDoctor ?? []).length === 0 ? <Empty>No patients in this period.</Empty> : (
                <ReportTable cols={[{ label: 'Doctor' }, { label: 'Total', numeric: true }, { label: 'New', numeric: true }, { label: 'Returning', numeric: true }, { label: 'Return rate', numeric: true }]}>
                  {(patientData?.byDoctor ?? []).map(r => (
                    <tr key={r.doctor_id ?? 'unassigned'} className={ROW}>
                      <td className={cn(tdFirst, 'text-foreground')}>{r.doctor_name ?? 'Unassigned'}</td>
                      <Num value={r.patient_count} className="font-semibold" />
                      <Num value={r.new_count} />
                      <Num value={r.returning_count} />
                      <Num value={pct(r.returning_count, r.patient_count)} last />
                    </tr>
                  ))}
                </ReportTable>
              )}
            </ReportCard>
          </div>
        </TabsContent>

        {/* ── Appointments ────────────────────────────────────────────────────── */}
        <TabsContent value="appointments" className="flex-1 overflow-auto px-4 sm:px-6 py-4 space-y-4 mt-0">
          <Figures loading={apptsLoading} items={[
            { label: 'Appointments', value: String(apptTotals.total), sub: `${apptTotals.upcoming} upcoming` },
            { label: 'Seen', value: String(apptTotals.completed), sub: pct(apptTotals.completed, apptTotals.total) },
            { label: 'Missed', value: String(apptTotals.no_show), sub: `${apptTotals.cancelled} cancelled` },
            // Of the bookings that were due (seen or missed) — cancelled and upcoming ones can't be missed yet
            { label: 'Missed rate', value: pct(apptTotals.no_show, apptTotals.completed + apptTotals.no_show) },
          ]} />

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            <ReportCard
              title="Daily footfall"
              chart={apptsLoading ? <ChartLoading /> : footfall.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={footfallChartData}>
                    <CartesianGrid {...GRID} />
                    <XAxis dataKey="date" tickFormatter={dateTick} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                    <YAxis tick={AXIS_TICK} width={30} allowDecimals={false} axisLine={false} tickLine={false} />
                    <Tooltip {...TOOLTIP} labelFormatter={dateTick} cursor={{ stroke: 'hsl(var(--border))' }} />
                    <Legend {...LEGEND} />
                    <Line type="monotone" dataKey="total" name="Total" stroke={C.slate} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="completed" name="Seen" stroke={C.primary} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="no_show" name="Missed" stroke={C.alert} strokeWidth={1.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : undefined}
            >
              {apptsLoading ? <SkeletonRows /> : footfall.length === 0 ? <Empty>No appointments in this period.</Empty> : (
                <ReportTable cols={[{ label: 'Date' }, { label: 'Total', numeric: true }, { label: 'Seen', numeric: true }, { label: 'Missed', numeric: true }, { label: 'Cancelled', numeric: true }, { label: 'Upcoming', numeric: true }]}>
                  {footfall.map(f => (
                    <tr key={f.date} className={ROW}>
                      <td className={cn(tdFirst, 'whitespace-nowrap text-foreground tabular-nums')}>{niceDate(f.date)}</td>
                      <Num value={f.total} className="font-semibold" />
                      <Num value={f.completed} />
                      <Num value={f.no_show} className={Number(f.no_show) === 0 ? 'text-muted-foreground' : undefined} />
                      <Num value={f.cancelled} className={Number(f.cancelled) === 0 ? 'text-muted-foreground' : undefined} />
                      <Num value={f.upcoming} last className={Number(f.upcoming) === 0 ? 'text-muted-foreground' : undefined} />
                    </tr>
                  ))}
                </ReportTable>
              )}
            </ReportCard>

            <ReportCard
              title="By doctor"
              chart={apptsLoading ? <ChartLoading /> : doctorApptsChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={doctorApptsChartData} margin={{ bottom: 20 }}>
                    <CartesianGrid {...GRID} />
                    <XAxis dataKey="doctor" tick={AXIS_TICK} angle={-20} textAnchor="end" height={40} interval={0} axisLine={false} tickLine={false} />
                    <YAxis tick={AXIS_TICK} width={30} allowDecimals={false} axisLine={false} tickLine={false} />
                    <Tooltip {...TOOLTIP} />
                    <Legend {...LEGEND} />
                    <Bar dataKey="total" name="Total" fill={C.slate} radius={BAR_RADIUS} />
                    <Bar dataKey="no_show" name="Missed" fill={C.alert} radius={BAR_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              ) : undefined}
            >
              {apptsLoading ? <SkeletonRows /> : doctorAppts.length === 0 ? <Empty>No appointments in this period.</Empty> : (
                <ReportTable cols={[{ label: 'Doctor' }, { label: 'Total', numeric: true }, { label: 'Seen', numeric: true }, { label: 'Missed', numeric: true }, { label: 'Missed rate', numeric: true }]}>
                  {doctorAppts.map(d => (
                    <tr key={d.doctor_id ?? 'unassigned'} className={ROW}>
                      <td className={cn(tdFirst, 'text-foreground')}>{d.doctor_name ?? 'Unassigned'}</td>
                      <Num value={d.total_appointments} className="font-semibold" />
                      <Num value={d.completed_count} />
                      <Num value={d.no_show_count} className={Number(d.no_show_count) === 0 ? 'text-muted-foreground' : undefined} />
                      <Num value={pct(Number(d.no_show_count), Number(d.completed_count) + Number(d.no_show_count))} last />
                    </tr>
                  ))}
                </ReportTable>
              )}
            </ReportCard>
          </div>
        </TabsContent>

        {/* ── IPD ─────────────────────────────────────────────────────────────── */}
        {canViewIpd && (
          <TabsContent value="ipd" className="flex-1 overflow-auto px-4 sm:px-6 py-4 space-y-4 mt-0">
            <Figures loading={ipdLoading} items={[
              { label: 'Beds', value: String(occupancyTotals.total_beds) },
              { label: 'Occupied', value: String(occupancyTotals.occupied_beds) },
              { label: 'Available', value: String(occupancyTotals.total_beds - occupancyTotals.occupied_beds) },
              { label: 'Occupancy', value: pct(occupancyTotals.occupied_beds, occupancyTotals.total_beds), sub: 'right now' },
              { label: 'Average stay', value: los?.avgLengthOfStay != null ? `${los.avgLengthOfStay.toFixed(1)} days` : '—', sub: `${los?.dischargedCount ?? 0} discharged in these dates` },
              { label: 'Active admissions', value: String(los?.activeAdmissions ?? 0) },
            ]} />

            <ReportCard
              title="Occupancy by ward"
              meta="Right now — not limited to the dates above"
              chart={ipdLoading ? <ChartLoading /> : occupancyChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={occupancyChartData} margin={{ bottom: 20 }}>
                    <CartesianGrid {...GRID} />
                    <XAxis dataKey="ward" tick={AXIS_TICK} angle={-20} textAnchor="end" height={40} interval={0} axisLine={false} tickLine={false} />
                    <YAxis tick={AXIS_TICK} width={30} allowDecimals={false} axisLine={false} tickLine={false} />
                    <Tooltip {...TOOLTIP} />
                    <Legend {...LEGEND} />
                    <Bar dataKey="total" name="Beds" fill={C.slate} radius={BAR_RADIUS} />
                    <Bar dataKey="occupied" name="Occupied" fill={C.primary} radius={BAR_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              ) : undefined}
            >
              {ipdLoading ? <SkeletonRows /> : occupancy.length === 0 ? <Empty>No wards configured.</Empty> : (
                <ReportTable cols={[{ label: 'Ward' }, { label: 'Beds', numeric: true }, { label: 'Occupied', numeric: true }, { label: 'Available', numeric: true }, { label: 'Occupancy', numeric: true }]}>
                  {occupancy.map(w => (
                    <tr key={w.ward_id} className={ROW}>
                      <td className={cn(tdFirst, 'text-foreground')}>{w.ward_name}</td>
                      <Num value={w.total_beds} />
                      <Num value={w.occupied_beds} />
                      <Num value={w.total_beds - w.occupied_beds} className={w.total_beds - w.occupied_beds === 0 ? 'text-highlight-foreground font-semibold' : undefined} />
                      <Num value={pct(w.occupied_beds, w.total_beds)} last className="font-semibold" />
                    </tr>
                  ))}
                </ReportTable>
              )}
            </ReportCard>
          </TabsContent>
        )}
      </div>
    </Tabs>
  );
}
